
import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AsyncErrorState } from '@/components/ui/async-error-state';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { apiClient } from '@/lib/api/client';
import type { EventCategory, EventResponse } from '@/lib/shared-types/api/endpoints/events';

const categories: Array<{ label: string; value: EventCategory | 'all' }> = [
  { label: 'All events', value: 'all' },
  { label: 'Services', value: 'service' },
  { label: 'Conferences', value: 'conference' },
  { label: 'Prayer', value: 'prayer' },
  { label: 'Outreach', value: 'outreach' },
  { label: 'Other', value: 'other' },
];

interface EventsData {
  events: EventResponse[];
}

export default function EventsPage() {
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [category, setCategory] = useState<EventCategory | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestKey, setRequestKey] = useState(0);

  useEffect(() => {
    let active = true;
    const loadEvents = async () => {
      setLoading(true);
      setError(null);
      const query = category === 'all' ? '' : `?category=${encodeURIComponent(category)}`;
      try {
        const response = await apiClient.get<EventsData>(`/events${query}`, { skipAuth: true });
        if (!active) return;
        if (response.success && response.data && Array.isArray(response.data.events)) {
          setEvents(response.data.events);
        } else {
          setError(response.error?.message ?? 'Events could not be loaded. Please try again.');
        }
      } catch {
        if (!active) return;
        setError('Events could not be loaded. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadEvents();
    return () => { active = false; };
  }, [category, requestKey]);

  const visibleEvents = useMemo(
    () => [...events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [events],
  );

  const retry = () => setRequestKey((key) => key + 1);

  return (
    <main className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <header className="max-w-3xl space-y-4">
        <Badge variant="outline" className="border-primary/30 bg-primary/5">Gather together</Badge>
        <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">Upcoming events</h1>
        <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
          Find a place to worship, learn, pray, and serve with the Dominion City community.
        </p>
      </header>

      <section aria-label="Filter events" className="flex flex-wrap gap-2">
        {categories.map((item) => (
          <Button
            key={item.value}
            type="button"
            variant={category === item.value ? 'default' : 'outline'}
            aria-pressed={category === item.value}
            onClick={() => setCategory(item.value)}
            className="min-h-11"
          >
            {item.label}
          </Button>
        ))}
      </section>

      {loading ? (
        <div role="status" aria-label="Loading events" className="flex min-h-48 items-center justify-center">
          <LoadingSpinner size="lg" />
          <span className="sr-only">Loading events</span>
        </div>
      ) : error ? (
        <AsyncErrorState title="Events unavailable" message={error} onRetry={retry} />
      ) : visibleEvents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-6 py-16 text-center">
          <CalendarDays className="mx-auto mb-4 h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-xl font-semibold">No upcoming events</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
            There are no events in this category right now. Check back soon or explore another category.
          </p>
          {category !== 'all' && (
            <Button variant="outline" className="mt-5 min-h-11" onClick={() => setCategory('all')}>
              View all events
            </Button>
          )}
        </div>
      ) : (
        <section aria-label="Upcoming events" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleEvents.map((event) => (
            <Card key={event.id} className="overflow-hidden border-border/70 shadow-none">
              {event.bannerUrl ? (
                <img src={event.bannerUrl} alt="" className="aspect-[16/9] w-full object-cover" loading="lazy" />
              ) : (
                <div className="flex aspect-[16/9] items-center justify-center bg-muted" aria-hidden="true">
                  <CalendarDays className="h-8 w-8 text-muted-foreground/70" />
                </div>
              )}
              <CardContent className="space-y-3 p-5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <Badge variant="secondary" className="capitalize">{event.category}</Badge>
                  <time className="text-sm text-muted-foreground" dateTime={event.date}>
                    {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(event.date))}
                  </time>
                </div>
                <h2 className="text-xl font-semibold leading-snug">{event.title}</h2>
                {event.location && (
                  <p className="flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{event.location}</span>
                  </p>
                )}
                {event.description && <p className="line-clamp-3 text-sm leading-6 text-muted-foreground">{event.description}</p>}
                {event.registerUrl && (
                  <Button asChild variant="outline" className="min-h-11 w-full">
                    <a href={event.registerUrl} target="_blank" rel="noopener noreferrer">Register</a>
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </section>
      )}
    </main>
  );
}
